import React, { useEffect, useRef, useState } from "react";
import { X, Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";

type ProfileShareModalProps = {
  open: boolean;
  username: string | null; // MUST be canonical username (never displayName / uid)
  onClose: () => void;
};

export const ProfileShareModal: React.FC<ProfileShareModalProps> = ({ open, username, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [qrFail, setQrFail] = useState(false);
  const [copied, setCopied] = useState<null | "link">(null);

  // Only build URL when we truly have a username
  const profileUrl = username ? `https://www.spellgather.com/${encodeURIComponent(username)}` : "";

  useEffect(() => {
    if (!open || !profileUrl) return;
    let cancelled = false;
    (async () => {
      setQrFail(false);
      if (!canvasRef.current) return;
      try {
        // @ts-ignore
        const QR = await import("qrcode");
        if (cancelled) return;
        await QR.toCanvas(canvasRef.current, profileUrl, {
          errorCorrectionLevel: "M",
          width: 260,
          margin: 1,
          color: { dark: "#000000", light: "#ffffff" },
        });
      } catch {
        if (!cancelled) setQrFail(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, profileUrl]);

  if (!open) return null;

  async function handleCopy() {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied("link");
      setTimeout(() => setCopied(null), 1400);
    } catch {}
  }

  async function handleShare() {
    if (!profileUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${username}'s Spellgather Profile`,
          text: `Check out ${username}'s collection on Spellgather.`,
          url: profileUrl,
        });
      } catch {}
    } else {
      handleCopy();
    }
  }

  const titleUsername = username ? `“${username}”` : "";

  return (
    <>
      <div
        className="fixed inset-0 z-[250] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed inset-0 z-[251] p-4 flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-share-title"
      >
        <div className="relative w-full max-w-sm rounded-xl border border-white/15 bg-gray-900/95 backdrop-blur shadow-2xl p-5 flex flex-col gap-5">
          <div className="flex items-start justify-between">
            <div className="pr-4">
              <h2 id="profile-share-title" className="text-base font-semibold text-white">
                Share my profile {titleUsername}
              </h2>
              <p className="text-xs text-white/60 mt-1">
                {username ? "Scan or share your public profile link." : "Username not available."}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close"
              className="text-white/70 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {username && (
            <>
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-lg bg-white p-3 shadow-inner w-[300px] h-[300px] flex items-center justify-center">
                  {qrFail ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(profileUrl)}`}
                      alt="QR Code"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <canvas
                      ref={canvasRef}
                      aria-label="QR code for profile link"
                      className="w-full h-full"
                    />
                  )}
                </div>
                <div className="w-full text-center">
                  <a
                    href={profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-[13px] text-emerald-300 hover:underline"
                  >
                    {profileUrl}
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold"
                  onClick={handleCopy}
                  disabled={!profileUrl}
                >
                  <Copy className="w-4 h-4 mr-1" />
                  {copied === "link" ? "Copied!" : "Copy Link"}
                </Button>
                <Button
                  className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"
                  onClick={handleShare}
                  disabled={!profileUrl}
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </Button>
              </div>
              <div className="text-[11px] text-white/40 text-center">
                Public link is view-only.
              </div>
            </>
          )}

          {!username && (
            <div className="text-[12px] text-amber-300 text-center py-6">
              A username is required to share a profile.
            </div>
          )}
        </div>
      </div>
    </>
  );
};
